<template>
  <div class="prompt-editor bg-white br-10px">
    <div class="editor-header">
      <h3>{{ template.name }}</h3>
      <p class="description">{{ template.description }}</p>
      <span class="version-badge">Version {{ template.version }}</span>
    </div>

    <div class="editor-fields">
      <div class="field">
        <AppLabel for="system-prompt">System Prompt <span class="optional">(optional)</span></AppLabel>
        <AppInput
          id="system-prompt"
          v-model="systemPrompt"
          type="textarea"
          placeholder="System instructions for the AI (e.g., role, tone, constraints)"
          :rows="6"
          class="prompt-textarea"
        />
      </div>

      <div class="field">
        <AppLabel for="user-prompt" required>User Prompt</AppLabel>
        <AppInput
          id="user-prompt"
          v-model="userPrompt"
          type="textarea"
          placeholder="Main prompt template with {{variables}}"
          :rows="20"
          class="prompt-textarea"
        />
        <p class="hint">Use <code v-pre>{{variable_name}}</code> syntax to insert variables</p>
      </div>
    </div>

    <div class="editor-footer">
      <AppButton :disabled="!hasChanges" @click="handleSave">Save Changes</AppButton>
      <AppButton variant="outlined" :disabled="!hasChanges" @click="handleDiscard">Discard Changes</AppButton>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { AppButton, AppInput, AppLabel } from "@/components/ui";

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["save"]);

const systemPrompt = ref("");
const userPrompt = ref("");
const originalSystem = ref("");
const originalUser = ref("");

// Initialize values when template changes
watch(
  () => props.template,
  (newTemplate) => {
    if (newTemplate) {
      systemPrompt.value = newTemplate.system_prompt || "";
      userPrompt.value = newTemplate.user_prompt || "";
      originalSystem.value = newTemplate.system_prompt || "";
      originalUser.value = newTemplate.user_prompt || "";
    }
  },
  { immediate: true }
);

const hasChanges = computed(() => {
  return systemPrompt.value !== originalSystem.value || userPrompt.value !== originalUser.value;
});

const handleSave = () => {
  emit("save", {
    systemPrompt: systemPrompt.value,
    userPrompt: userPrompt.value,
  });
  // Update originals after save
  originalSystem.value = systemPrompt.value;
  originalUser.value = userPrompt.value;
};

const handleDiscard = () => {
  systemPrompt.value = originalSystem.value;
  userPrompt.value = originalUser.value;
};
</script>

<style lang="scss" scoped>
.prompt-editor {
  overflow: hidden;
}

.editor-header {
  padding: 24px;
  border-bottom: 1px solid $color-border;

  h3 {
    margin: 0 0 6px 0;
  }
}

.description {
  margin: 0 0 10px 0;
  color: $color-text-muted;
  font-size: 14px;
}

.version-badge {
  display: inline-block;
  padding: 3px 8px;
  background: $color-grey-12;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: $color-text-muted;
}

.editor-fields {
  padding: 24px;
}

.field {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
}

.optional {
  font-weight: 400;
  color: $color-text-muted;
  font-size: 13px;
}

.prompt-textarea :deep(.el-textarea__inner) {
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  font-size: 13px;
  line-height: 1.5;
}

.hint {
  margin: 8px 0 0 0;
  font-size: 13px;
  color: $color-text-muted;

  code {
    background: $color-grey-12;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 13px;
  }
}

.editor-footer {
  padding: 16px 24px;
  background: $color-surface-muted;
  border-top: 1px solid $color-border;
  display: flex;
  gap: 8px;
}
</style>
